import React, { useEffect } from 'react';

import apolloWrapper from '@hoc/apolloWrapper';
import { IndexSuggestionItemsBlock, IndexSuggestionItemsBlockGridProps } from '@components/IndexSuggestionItemsBlock';
import { edgesToProducts } from '@utils/products';
import { logger } from '@utils/logger';
import {
  ClerkActionType,
  ClerkCategoryType,
  ClerkGetMoreSuggestionsEvent,
  ClerkShowSuggestionBlockEvent,
  ClerkSourceType,
  useClerk,
} from '@hook/useClerk';

import { useIndexSuggestionQuery } from './graphql.v3.generated';

const COUNT_OF_FETCHED_PRODUCTS = 30;

interface Props {
  maxCount?: number,
  grid?: IndexSuggestionItemsBlockGridProps,
  onProductClick?: () => void,
}

export const IndexSuggestionItemsBlockContainer: React.FC<Props> = ({
  maxCount,
  grid,
  onProductClick,
}) => {
  const { sendClerkEvent } = useClerk();

  const { data, loading, fetchMore } = useIndexSuggestionQuery({
    variables: {
      first: maxCount || COUNT_OF_FETCHED_PRODUCTS,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const products = data?.indexSuggestions?.products;
  const hasNextPage = products?.pageInfo?.hasNextPage;
  const productsLength = products?.edges.length || 0;

  const handleLoadMore = async () => {
    if (hasNextPage && !maxCount) {
      await fetchMore({
        variables: {
          after: products?.pageInfo?.endCursor,
        },
      });
    }
  };

  useEffect(() => {
    sendClerkEvent<ClerkShowSuggestionBlockEvent>({
      category: ClerkCategoryType.suggestionBlock,
      action: ClerkActionType.show,
      payload: {
        source: ClerkSourceType.personalSuggestionsBlock,
      },
    });
  }, [sendClerkEvent]);

  useEffect(() => {
    if (productsLength > COUNT_OF_FETCHED_PRODUCTS) {
      // send event only if get more products
      sendClerkEvent<ClerkGetMoreSuggestionsEvent>({
        category: ClerkCategoryType.suggestionBlock,
        action: ClerkActionType.scroll,
        payload: {
          source: ClerkSourceType.personalSuggestionsBlock,
        },
      });
    }
  }, [productsLength, sendClerkEvent]);

  if (loading || products?.edges) {
    const productsList = edgesToProducts(products?.edges || []);

    return (
      <IndexSuggestionItemsBlock
        products={productsList || []}
        loading={loading}
        hasNextPage={maxCount ? false : !!hasNextPage}
        maxCount={maxCount}
        grid={grid}
        onLoadMore={handleLoadMore}
        onProductClick={onProductClick}
      />
    );
  }

  logger.error('Empty render');
  return null;
};

export default apolloWrapper(IndexSuggestionItemsBlockContainer);
